import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { CalendarDays, FileSpreadsheet, FileText, Folder, Link2, Mail, Pin, Presentation, Trash2, Video } from 'lucide-react';
import { api, type CalEvent, type DriveFile, type Mail as MailT, type Resource } from '../api.ts';
import { useSave } from '../data.ts';
import { ago, fmtWhen } from '../util.ts';
import { Empty, OpsMark } from './ui.tsx';

const kindIcon = (k: string) => (k === 'folder' ? Folder : k === 'sheet' ? FileSpreadsheet : k === 'slides' ? Presentation : k === 'doc' ? FileText : Link2);
const useWs = () => useQuery({ queryKey: ['ws-status'], queryFn: () => api<{ connected: boolean; scopes: string[] }>('/workspace/status'), staleTime: 60_000 });

// The "SharePoint" layer: pinned company folders and docs, plus what changed in Drive.
export function WorkspaceHub({ resources }: { resources: Resource[] }) {
  const ws = useWs();
  const recent = useQuery({ queryKey: ['drive-recent'], queryFn: () => api<{ connected: boolean; files: DriveFile[] }>('/workspace/files?recent=1'), enabled: !!ws.data?.connected, staleTime: 120_000, retry: false });
  const save = useSave();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState(''); const [url, setUrl] = useState('');
  return (
    <section className="card">
      <div className="card-h"><h2><Pin size={14} aria-hidden /> Workspace</h2><Link className="sm" to="/workspace">Open Drive explorer →</Link></div>
      <ul className="res">
        {resources.map((r) => { const Icon = kindIcon(r.kind); return (
          <li key={r.id}><Icon size={15} aria-hidden /><a href={r.url} target="_blank" rel="noreferrer">{r.title}</a>
            {r.pinned_by === 'operator@agents.gbsoft.dev' && <OpsMark size={12} className="agent-ic" aria-label="Pinned by Ops" />}
            <button className="icon" aria-label={`Unpin ${r.title}`} onClick={() => save.mutate({ path: `/resources/${r.id}`, method: 'DELETE' })}><Trash2 size={13} /></button></li>
        ); })}
      </ul>
      {!resources.length && <Empty>Pin the folders and docs you open every week: Rev.B specs, legal, partner decks.</Empty>}
      {adding ? (
        <form className="row" onSubmit={async (e) => { e.preventDefault(); await save.mutateAsync({ path: '/resources', body: { title, url } }); setTitle(''); setUrl(''); setAdding(false); }}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name" aria-label="Name" required />
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/…" aria-label="Link" required />
          <button className="btn primary sm">Pin</button>
        </form>
      ) : <button className="btn sm" onClick={() => setAdding(true)}><Pin size={13} aria-hidden /> Pin a link</button>}
      {ws.data?.connected && (
        <>
          <h3 className="sub">Recently changed in Drive</h3>
          <ul className="res">
            {(recent.data?.files ?? []).slice(0, 7).map((f) => (
              <li key={f.id}><img src={f.iconLink} alt="" width={15} height={15} /><a href={f.webViewLink} target="_blank" rel="noreferrer">{f.name}</a><span className="muted sm">{ago(f.modifiedTime)}</span></li>
            ))}
          </ul>
        </>
      )}
      {ws.data && !ws.data.connected && <p className="muted sm"><a href="/auth/workspace/connect">Connect Google Workspace</a> to see Drive, Calendar and Gmail here.</p>}
    </section>
  );
}

// Upcoming meetings and action-worthy email, read as you.
export function TeamPulse() {
  const ws = useWs();
  const cal = useQuery({ queryKey: ['calendar'], queryFn: () => api<{ connected: boolean; events: CalEvent[] }>('/workspace/calendar?days=7'), enabled: !!ws.data?.connected, retry: false, staleTime: 120_000 });
  const mail = useQuery({ queryKey: ['mail'], queryFn: () => api<{ connected: boolean; messages: MailT[] }>('/workspace/mail'), enabled: !!ws.data?.connected, retry: false, staleTime: 300_000 });
  const needsGmail = ws.data?.connected && !ws.data.scopes.includes('https://www.googleapis.com/auth/gmail.readonly');
  if (!ws.data?.connected) return null;
  return (
    <section className="card">
      <div className="card-h"><h2><CalendarDays size={14} aria-hidden /> Team pulse</h2></div>
      <ul className="agenda">
        {(cal.data?.events ?? []).slice(0, 6).map((e) => (
          <li key={e.id}>
            <span className="when">{e.allDay ? new Date(`${e.start}T00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : fmtWhen(e.start)}</span>
            <a href={e.link} target="_blank" rel="noreferrer">{e.title}</a>
            {e.byAgent && <OpsMark size={12} className="agent-ic" aria-label="Placed by Ops" />}
            {e.meet && <a className="icon" href={e.meet} target="_blank" rel="noreferrer" aria-label="Join Meet"><Video size={13} /></a>}
          </li>
        ))}
        {cal.data && !cal.data.events.length && <Empty>No meetings in the next 7 days.</Empty>}
      </ul>
      <h3 className="sub"><Mail size={12} aria-hidden /> Flagged email</h3>
      {needsGmail ? <p className="muted sm"><a href="/auth/workspace/connect">Reconnect Workspace</a> and allow Gmail (read-only) to surface action items.</p> : (
        <ul className="mail">
          {(mail.data?.messages ?? []).slice(0, 5).map((m) => (
            <li key={m.id}><a href={m.url} target="_blank" rel="noreferrer"><b>{m.subject || '(no subject)'}</b></a><span className="muted sm">{m.from.replace(/<.*>/, '').trim()} · {m.snippet.slice(0, 90)}</span></li>
          ))}
          {mail.data && !mail.data.messages.length && <Empty>Nothing important in the last week.</Empty>}
        </ul>
      )}
    </section>
  );
}
